
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../shared/store';
import { Row, Col, Form, FloatingLabel, Button, Card, Collapse } from 'react-bootstrap';
import { Icon } from '../../../../components';
import { Select } from '../../../../components/components/Select/Select';
import './CarrierDetailsForm.scss';
import { CarrierDetail } from '../../Model/CarrierDetail';
import * as constant from '../../../../shared/util/constant';
import * as label from '../../../../shared/util/label';
import CarrierAddressList from './CarrierAddressList';
import AgencyMasterCodesGrid from '../../AgencyMasterCodes/AgencyMasterCodesGrid';


interface CarrierSectionProps {
  title: string;
  className?: string;
  children: React.ReactNode;
}
const CarrierSection: React.FC<CarrierSectionProps> = ({ title, className = '', children }) => {
  const [open, setOpen] = useState(true);
  return (
    <Card className={`border customerPanel cstPanel ${className}`}>
      <Card.Header
        className="carrier-section-header bg-transparent py-3 border-bottom-0"
        onClick={() => setOpen(o => !o)}
      >
        <Icon icon={open ? 'down-arrow' : 'right-arrow'} size="xs" className="me-2" />
        <span className="roboto-condensed-bold">{title}</span>
      </Card.Header>
      <Collapse in={open}>
        <div>
          <Card.Body className="pt-0">
            {children}
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export type CarrierFormMode = 'view' | 'edit' | 'create';

interface CarrierDetailsFormProps {
  mode: CarrierFormMode;
  carrierDetail: CarrierDetail;
  onChange: (field: string, value: any) => void;
  onSave: () => void;
  onCancel: () => void;
  parentCompanyName?: string;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

const CarrierDetailsForm: React.FC<CarrierDetailsFormProps> = ({ mode, carrierDetail, onChange, onSave, onCancel, parentCompanyName, canCreate = true, canUpdate = true, canDelete = true }) => {
  const isEditable = mode === 'edit' || mode === 'create';

  // Use a formValidation object for explicit field validation
  const [formValidation, setFormValidation] = useState<any>({});
  useEffect(() => {
    setFormValidation({});
  }, [mode]);

  const companyTypeList = useSelector((state: RootState) => state.lookup.companyTypeList);
  const typeOptions = companyTypeList.map(item => ({ value: item.id, label: item.lookupValue }));

  // Validation function
  const validateForm = () => {
    const errors: any = {};
    if (!carrierDetail.name || !carrierDetail.name.trim()) {
      errors.name = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
    }
    if (!carrierDetail.type || !carrierDetail.type.trim()) {
      errors.type = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
    }
    return errors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    setFormValidation(errors);
    if (Object.keys(errors).length === 0) {
      onSave();
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    onChange(field, value);
    setFormValidation((prev: any) => {
      // Only update the error for the changed field
      if ((field === 'name' && (!value || !value.trim())) ||
          (field === 'type' && (!value || !value.trim()))) {
        return {
          ...prev,
          [field]: { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK }
        };
      } else {
        // Remove only the error for this field, keep others
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
    });
  };

  // Show validation for a field on blur if invalid, keep other errors
  const handleFieldBlur = (field: string, value: any) => {
    setFormValidation((prev: any) => {
      // Only set error for the blurred field if invalid, keep others as is
      if ((field === 'name' && (!value || !value.trim()))) {
        return {
          ...prev,
          name: { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK }
        };
      }
      if ((field === 'type' && (!value || !value.trim()))) {
        return {
          ...prev,
          type: { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK }
        };
      }
      // If valid, do not remove error here (only on change)
      return prev;
    });
  };

  const handleCancelClick = () => {
    setFormValidation({});
    onCancel();
  };

  return (
    <div className="carrier-details-sections mt-2">
      <CarrierSection title={label.CarrierManagementLabels.CONTACT_INFORMATION_TITLE}>
    <Form className="carrier-details-form" noValidate onSubmit={handleSubmit}>
      <Row>
        <Col md={6} className="mb-3">
          <FloatingLabel controlId="carrierName" label={<span>Carrier Name<span className="text-danger ms-1">*</span></span>}>
            <Form.Control
              type="text"
              value={carrierDetail.name || ''}
              onChange={e => handleFieldChange('name', e.target.value)}
              onBlur={e => handleFieldBlur('name', e.target.value)}
              disabled={!isEditable}
              required
              className="mb-3"
              isInvalid={formValidation['name']?.isValid === false}
              maxLength={150}
            />
            <Form.Control.Feedback type="invalid">
              {formValidation['name']?.warningText}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Col>
        <Col md={6} className="mb-3">
          <FloatingLabel controlId="naic" label="NAIC">
            <Form.Control
              type="text"
              value={carrierDetail.naic || ''}
              onChange={e => handleFieldChange('naic', e.target.value)}
              disabled={!isEditable}
              className="mb-3"
              maxLength={6}
            />
          </FloatingLabel>
        </Col>
      </Row>
      <Row>
        <Col md={6} className="mb-3">
          <div className="texbox-input-container">
            <Select
              id="carrier-type-select"
              label={<span>Type</span>}
              options={typeOptions}
              defaultSelected={typeOptions.filter(opt => opt.value === carrierDetail.type)}
              onChange={([selected]) => handleFieldChange('type', selected)}
              disabled={!isEditable}
              isInvalid={formValidation['type']?.isValid === false}
              invalidWarning={formValidation['type']?.warningText}
              isRequired
              showAsFloatingLabel
            />
          </div>
        </Col>
        <Col md={3} className="mb-3">

          <Form.Check
            checked={carrierDetail.isActive}
            onChange={e => handleFieldChange('isActive', e.target.checked)}
            disabled={!isEditable}
            className="mb-3"
            label="IsActive"
          >
          </Form.Check>
        </Col>
      </Row>
      
      {(carrierDetail.id === carrierDetail.parentCompanyId || !carrierDetail.parentCompanyId) && <Row>
        
        <Col md={6} className="mb-3">
          <FloatingLabel controlId="originAddr" label="IBM Address">
            <Form.Control
              type="text"
              value={carrierDetail.ivansSenderBoxAddresses?.[0]?.originAddr || ''}
              onChange={e => {
                const updated = [...(carrierDetail.ivansSenderBoxAddresses ?? [])];
                if (updated.length > 0) {
                  updated[0] = { ...updated[0], originAddr: e.target.value };
                } else {
                  updated.push({ originAddr: e.target.value });
                }
                onChange('ivansSenderBoxAddresses', updated);
              }}
              disabled={!isEditable}
              className="mb-3"
              maxLength={18}
            />
          </FloatingLabel>
        </Col>
        <Col md={6} className="mb-3">
          <FloatingLabel controlId="informalSenderName" label="Informal Sender Name">
            <Form.Control
              type="text"
              value={carrierDetail.informalSenderName || ''}
              onChange={e => handleFieldChange('informalSenderName', e.target.value)}
              disabled={!isEditable}
              className="mb-3"
              maxLength={150}
            />
          </FloatingLabel>
        </Col>
      </Row>}
      <CarrierAddressList
        mode={mode}
        addresses={carrierDetail.carrierAddress ?? []}
        onChange={updated => onChange('carrierAddress', updated)}
        canCreate={canCreate}
        canDelete={canDelete}
      />

      <Row>
        <Col md={12} className="form-actions mt-3 pull-right">
          {isEditable ? (
            <>
              <Button
                type="submit"
                className='btn btn-primary mr-2 action-btn-base'
                disabled={
                  !carrierDetail.name ||
                  !carrierDetail.type ||
                  (mode === 'edit' && !canUpdate) ||
                  (mode === 'create' && !canCreate)
                }
              >
                {label.SAVE.toLocaleUpperCase()}
              </Button>
              <Button type="button" onClick={handleCancelClick} variant="outline-primary">{label.CANCEL.toLocaleUpperCase()}</Button>
            </>
          ) : (
            <div>
              <Button type="button" onClick={() => onChange('edit', true)} variant="outline-primary" disabled={!canUpdate}>{label.EDIT.toLocaleUpperCase()}</Button>
            </div>
          )}
        </Col>
      </Row>
    </Form>
      </CarrierSection>

      {carrierDetail.id && (carrierDetail.id === carrierDetail.parentCompanyId || !carrierDetail.parentCompanyId) && (
        <CarrierSection title={label.CarrierManagementLabels.AGENCY_MASTER_CODES_TITLE} className="mt-2">
          <AgencyMasterCodesGrid
            carrierId={carrierDetail.id}
          />
        </CarrierSection>
      )}
    </div>
  );
};

export default CarrierDetailsForm;
